// Per-page renderer for "industry-benchmark" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, fetchPageAI } from '../utils';

export async function renderBenchmark(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const res = await api.get('/benchmark/analysis');
  const d = res.data;
  const comparison = d.comparison || [];

  const overCats = comparison.filter(c => c.status === 'over').map(c => `- ${c.category}: +${c.delta_pct}% vs industry`).join('\n');
  const underCats = comparison.filter(c => c.status === 'under').map(c => `- ${c.category}: ${c.delta_pct}% vs industry`).join('\n');
  const dataContext = `Industry: ${d.industry}\nTotal Spend: ${cs}${d.grand_total?.toLocaleString()}\nOver-Spending: ${d.summary?.over_spending} categories\nAligned: ${d.summary?.aligned} categories\nUnder-Spending: ${d.summary?.under_spending} categories\n\nOver-Spending Categories:\n${overCats}\n\nUnder-Spending Categories:\n${underCats}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  // Summary KPIs
  autoTable(doc, {
    startY: y,
    head: [['Industry', 'Total Spend', 'Over-Spending', 'Aligned', 'Under-Spending']],
    body: [[
      d.industry || 'N/A',
      `${cs}${d.grand_total?.toLocaleString()}`,
      `${d.summary?.over_spending} categories`,
      `${d.summary?.aligned} categories`,
      `${d.summary?.under_spending} categories`,
    ]],
    styles: { fontSize: 8, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Grouped bar chart — org vs industry
  if (comparison.length > 0) {
    const chartItems = comparison.slice(0, 12);
    const chartH = 70;
    if (y + chartH > 260) { doc.addPage(); y = 15; }

    doc.setFontSize(9);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text('Spend Distribution: You vs Industry Average', 14, y + 4);
    y += 8;

    const marginLeft = 50;
    const chartW = pw - marginLeft - 14;
    const maxPct = Math.max(...chartItems.flatMap(c => [c.org_pct, c.industry_pct]), 1);
    const barH = 3.5;
    const rowGap = 7;

    chartItems.forEach((c, i) => {
      const rowY = y + i * rowGap;
      // Category label
      doc.setFontSize(5.5);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(60);
      doc.text(String(c.category || '').substring(0, 22), 14, rowY + 2.5);

      // Org bar (blue)
      const orgW = Math.max(1, (c.org_pct / maxPct) * chartW);
      doc.setFillColor(0, 31, 92);
      doc.roundedRect(marginLeft, rowY, orgW, barH, 0.5, 0.5, 'F');

      // Industry bar (grey)
      const indW = Math.max(1, (c.industry_pct / maxPct) * chartW);
      doc.setFillColor(180, 180, 190);
      doc.roundedRect(marginLeft, rowY + barH, indW, barH, 0.5, 0.5, 'F');

      // Percentage labels
      doc.setFontSize(5);
      doc.setTextColor(0, 31, 92);
      doc.text(`${c.org_pct}%`, marginLeft + orgW + 2, rowY + 3);
      doc.setTextColor(130);
      doc.text(`${c.industry_pct}%`, marginLeft + indW + 2, rowY + barH + 3);
    });
    y += chartItems.length * rowGap + 6;

    // Legend
    doc.setFillColor(0, 31, 92);
    doc.roundedRect(14, y, 8, 3, 0.5, 0.5, 'F');
    doc.setFontSize(6);
    doc.setTextColor(60);
    doc.text('Your Spend %', 24, y + 2.5);
    doc.setFillColor(180, 180, 190);
    doc.roundedRect(70, y, 8, 3, 0.5, 0.5, 'F');
    doc.text('Industry Avg %', 80, y + 2.5);
    y += 10;
  }

  // Detailed table
  if (comparison.length > 0) {
    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Category', 'Your %', 'Industry %', 'Delta', 'Status', 'AI Insight']],
      body: comparison.map(c => [
        c.category,
        `${c.org_pct}%`,
        `${c.industry_pct}%`,
        `${c.delta_pct > 0 ? '+' : ''}${c.delta_pct}%`,
        c.status?.toUpperCase(),
        (c.insight || '').substring(0, 60),
      ]),
      styles: { fontSize: 6.5 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
      columnStyles: {
        4: { fontStyle: 'bold' },
        5: { fontStyle: 'italic', textColor: [100, 100, 100] },
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
